import React, { useEffect, useState } from 'react';
import { MaxLoanCalculatorInformation } from '../../components/features/MaxLoanCalculatorInformation';
import { NhgResponse } from '../../models/NhgResponse';
import { API } from '../../services';

export type FormItem = { [key: string]: any };

export interface InterestProvider {
    logoUrl: string;
    hypotheekNaam: string;
    aanbiederId: string;
    aanbiederNaam: string;
    rentestand: number;
    hypotheekVorm: string;
    trend: string;
    uitgelicht: boolean;
    lookupId: string;
    rentevastePeriode: number;
}

interface FormContextProps {
    form: FormItem;
		nhg: NhgResponse | null;
    isLoading: boolean;
    hasServerError: boolean;
    selectedMortgageType: number;
    calculatedMortgageType: number;
    selectedPeriod: number;
    isNhg: boolean;
    selectedInterest: number;
    data: MaxLoanCalculatorInformation | null;
    updateForm: (items: FormItem) => void;
    setIsLoading: (state: boolean) => void;
    setIsNhg: (state: boolean) => void;
    setServerError: (state: boolean) => void;
    updateData: (data: MaxLoanCalculatorInformation) => void;
    setSelectedMortgageType: (id: number) => void;
    setCalculatedMortgageType: (id: number) => void;
    setSelectedInterest: (rate: number) => void;
    setSelectedPeriod: (period: number) => void;
}

export const saveStateToSession = (state: any) => {
    window.localStorage.setItem(`state.maxloancalculator`, JSON.stringify(state));
};

export const FormContext = React.createContext<FormContextProps>({
    form: {},
		nhg: null,
    isLoading: false,
    hasServerError: false,
    data: null,
    isNhg: false,
    selectedMortgageType: 4,
    calculatedMortgageType: 4,
    selectedInterest: 0,
    selectedPeriod: 10,

    updateForm: (items: FormItem) => {},
    setIsLoading: (state: boolean) => {},
    setIsNhg: (state: boolean) => {},
    setServerError: (state: boolean) => {},
    updateData: (data: MaxLoanCalculatorInformation) => {},
    setSelectedMortgageType: (id: number) => {},
    setCalculatedMortgageType: (id: number) => {},
    setSelectedInterest: (rate: number) => {},
    setSelectedPeriod: (period: number) => {},
});

export const FormProvider: React.FC = ({ children }) => {
    const [form, setForm] = useState({});
		const [nhg, setNhg] = useState<NhgResponse | null>(null);
		const [isLoading, setIsLoading] = useState(false);
    const [hasServerError, setServerError] = useState(false);
    const [data, setData] = useState<MaxLoanCalculatorInformation | null>(null);
    const [selectedMortgageType, setSelectedMortgageType] = useState(4);
    const [calculatedMortgageType, setCalculatedMortgageType] = useState(4);
    const [selectedInterest, setSelectedInterest] = useState(0);
    const [selectedPeriod, setSelectedPeriod] = useState(10);
    const [isNhg, setIsNhg] = useState(false);

    const storedState = window.localStorage.getItem(`state.maxloancalculator`);

		// debug
		useEffect(() => {
			API.get<NhgResponse>('nhg').then((res) => setNhg(res))
		}, [setNhg])

    function updateForm(items: FormItem) {
        const formObj = {
            ...form,
            ...items,
        };
        saveStateToSession(formObj);
        setForm(formObj);
    }

    function updateData(info: MaxLoanCalculatorInformation) {
        setData({ ...info });
    }

    useEffect(() => {
        const getStateFromSession = () => {
            if (storedState && form) {
                if (storedState !== JSON.stringify(form)) {
                    const storedStateObj = JSON.parse(storedState);
                    setSelectedInterest(storedStateObj.interest);
                    setSelectedMortgageType(storedStateObj.mortgageType);
                    setIsNhg(storedStateObj.isNhg);
                    setForm(storedStateObj);
                }
            }
        };
        if (storedState) {
            getStateFromSession();
        }
    });

    return (
        <FormContext.Provider
            value={{
                isLoading,
                hasServerError,
                form,
								nhg,
                isNhg,
                updateForm,
                setIsLoading,
                setIsNhg,
                setServerError,
                selectedMortgageType,
                calculatedMortgageType,
                selectedInterest,
                selectedPeriod,
                setSelectedMortgageType,
                setSelectedPeriod,
                setCalculatedMortgageType,
                setSelectedInterest,
                data,
                updateData,
            }}>
            {children}
        </FormContext.Provider>
    );
};
